import { supabase } from './supabase';

const USE_MOCK_DATA = true;

interface SEOConnection {
  id?: string;
  client_id: string;
  website_name: string;
  website_url: string;
  ftp_host: string;
  ftp_port?: number;
  ftp_username: string;
  ftp_password: string;
  ftp_root_path?: string;
  is_sftp?: boolean;
  is_connected?: boolean;
  last_connected_at?: string;
  connection_error?: string;
  created_by_name?: string;
  created_at?: string;
  updated_at?: string;
}

class SEOService {
  /**
   * FTP Connection Management
   */
  async createConnection(connection: SEOConnection) {
    if (USE_MOCK_DATA) {
      console.log('[SEO] Mock mode: Would create connection', connection);
      return { success: true, data: { id: `conn_${Date.now()}`, ...connection } };
    }

    try {
      const { data, error } = await supabase
        .from('seo_connections')
        .insert([connection])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      console.error('[SEO] Error creating connection:', error);
      return { success: false, error: error.message };
    }
  }

  async getConnections(clientId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'conn_1',
            client_id: clientId,
            website_name: 'Company Website',
            website_url: 'https://example.com',
            ftp_host: 'ftp.example.com',
            ftp_port: 21,
            ftp_username: 'user',
            is_connected: true,
            last_connected_at: new Date().toISOString()
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('seo_connections')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[SEO] Error getting connections:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  async testConnection(connectionId: string) {
    if (USE_MOCK_DATA) {
      console.log('[SEO] Mock mode: Would test connection', connectionId);
      return { success: true, data: { connected: true } };
    }

    try {
      const { data, error } = await supabase
        .from('seo_connections')
        .update({
          is_connected: true,
          last_connected_at: new Date().toISOString(),
          connection_error: null
        })
        .eq('id', connectionId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      console.error('[SEO] Error testing connection:', error);
      return { success: false, error: error.message };
    }
  }

  async deleteConnection(connectionId: string) {
    if (USE_MOCK_DATA) {
      console.log('[SEO] Mock mode: Would delete connection', connectionId);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('seo_connections')
        .delete()
        .eq('id', connectionId);

      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('[SEO] Error deleting connection:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Page Management
   */
  async getPages(connectionId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'page_1',
            connection_id: connectionId,
            page_url: 'https://example.com/',
            page_path: '/',
            page_title: 'Home Page',
            page_type: 'home',
            seo_score: 85,
            has_issues: false
          },
          {
            id: 'page_2',
            connection_id: connectionId,
            page_url: 'https://example.com/about',
            page_path: '/about',
            page_title: 'About Us',
            page_type: 'page',
            seo_score: 72,
            has_issues: true
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('seo_pages')
        .select('*')
        .eq('connection_id', connectionId)
        .order('page_path');

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[SEO] Error getting pages:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  async createPage(page: any) {
    if (USE_MOCK_DATA) {
      console.log('[SEO] Mock mode: Would create page', page);
      return { success: true, data: { id: `page_${Date.now()}`, ...page } };
    }

    try {
      const { data, error } = await supabase
        .from('seo_pages')
        .insert([page])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      console.error('[SEO] Error creating page:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Meta Tags Management
   */
  async getMetaTags(pageId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: {
          id: 'meta_1',
          page_id: pageId,
          title: 'Example Page Title - Company Name',
          description: 'This is an example meta description that describes the page content in 120-160 characters.',
          keywords: ['example', 'demo', 'seo'],
          og_title: 'Example Page Title',
          og_description: 'This is the Open Graph description',
          og_image: 'https://example.com/og-image.jpg',
          twitter_card: 'summary_large_image',
          is_deployed: true
        }
      };
    }

    try {
      const { data, error } = await supabase
        .from('seo_meta_tags')
        .select('*')
        .eq('page_id', pageId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      console.error('[SEO] Error getting meta tags:', error);
      return { success: false, error: error.message };
    }
  }

  async saveMetaTags(metaTags: any) {
    if (USE_MOCK_DATA) {
      console.log('[SEO] Mock mode: Would save meta tags', metaTags);
      return { success: true, data: { id: `meta_${Date.now()}`, ...metaTags } };
    }

    try {
      const { data, error } = await supabase
        .from('seo_meta_tags')
        .upsert([metaTags])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      console.error('[SEO] Error saving meta tags:', error);
      return { success: false, error: error.message };
    }
  }

  async deployMetaTags(pageId: string, deployedBy: string) {
    if (USE_MOCK_DATA) {
      console.log('[SEO] Mock mode: Would deploy meta tags', pageId);
      return { success: true, data: { deployed: true } };
    }

    try {
      const { error: metaError } = await supabase
        .from('seo_meta_tags')
        .update({
          is_deployed: true,
          deployed_at: new Date().toISOString(),
          deployed_by: deployedBy
        })
        .eq('page_id', pageId);

      if (metaError) throw metaError;

      const { data: page } = await supabase
        .from('seo_pages')
        .select('client_id')
        .eq('id', pageId)
        .single();

      if (page) {
        await supabase.from('seo_deployment_log').insert([{
          page_id: pageId,
          client_id: page.client_id,
          deployment_type: 'meta_tags',
          changes: { deployed_by: deployedBy },
          status: 'success',
          deployed_by: deployedBy
        }]);
      }

      return { success: true, data: { deployed: true } };
    } catch (error: any) {
      console.error('[SEO] Error deploying meta tags:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Keywords Management
   */
  async getKeywords(clientId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'kw_1',
            keyword: 'digital marketing',
            current_rank: 12,
            target_rank: 5,
            search_volume: 12000,
            difficulty: 65,
            is_tracking: true
          },
          {
            id: 'kw_2',
            keyword: 'seo services',
            current_rank: 8,
            target_rank: 3,
            search_volume: 8500,
            difficulty: 72,
            is_tracking: true
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('seo_keywords')
        .select('*')
        .eq('client_id', clientId)
        .order('keyword');

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[SEO] Error getting keywords:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  async addKeyword(keyword: any) {
    if (USE_MOCK_DATA) {
      console.log('[SEO] Mock mode: Would add keyword', keyword);
      return { success: true, data: { id: `kw_${Date.now()}`, ...keyword } };
    }

    try {
      const { data, error } = await supabase
        .from('seo_keywords')
        .insert([keyword])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      console.error('[SEO] Error adding keyword:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * SEO Audit
   */
  async runAudit(connectionId: string, pageId?: string) {
    if (USE_MOCK_DATA) {
      console.log('[SEO] Mock mode: Would run audit', connectionId, pageId);
      return {
        success: true,
        data: {
          id: `audit_${Date.now()}`,
          overall_score: 78,
          meta_score: 85,
          content_score: 75,
          technical_score: 72,
          mobile_score: 80,
          speed_score: 70,
          critical_issues: 2,
          warnings: 5,
          recommendations: 8,
          issues: [
            { severity: 'critical', message: 'Missing meta description on 2 pages' },
            { severity: 'warning', message: 'Title tag too short on home page' }
          ]
        }
      };
    }

    try {
      const { data: connection } = await supabase
        .from('seo_connections')
        .select('client_id')
        .eq('id', connectionId)
        .single();

      if (!connection) throw new Error('Connection not found');

      const auditData: any = {
        connection_id: connectionId,
        page_id: pageId || null,
        client_id: connection.client_id,
        audit_type: pageId ? 'page' : 'full',
        overall_score: Math.floor(Math.random() * 30) + 70,
        meta_score: Math.floor(Math.random() * 20) + 80,
        content_score: Math.floor(Math.random() * 30) + 70,
        technical_score: Math.floor(Math.random() * 30) + 70,
        mobile_score: Math.floor(Math.random() * 20) + 75,
        speed_score: Math.floor(Math.random() * 30) + 60,
        critical_issues: Math.floor(Math.random() * 3),
        warnings: Math.floor(Math.random() * 8) + 2,
        recommendations: Math.floor(Math.random() * 10) + 5,
        issues: [],
        recommendations_data: [],
        audited_by: 'System'
      };

      const { data, error } = await supabase
        .from('seo_audits')
        .insert([auditData])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      console.error('[SEO] Error running audit:', error);
      return { success: false, error: error.message };
    }
  }

  async getAudits(clientId: string, limit = 10) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'audit_1',
            overall_score: 78,
            critical_issues: 2,
            warnings: 5,
            created_at: new Date().toISOString()
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('seo_audits')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[SEO] Error getting audits:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * SEO Summary
   */
  async getClientSummary(clientId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: {
          total_pages: 12,
          avg_seo_score: 78.5,
          pages_with_issues: 3,
          total_keywords: 25,
          tracking_keywords: 20,
          avg_keyword_rank: 15.3
        }
      };
    }

    try {
      const { data, error } = await supabase
        .rpc('get_client_seo_summary', { p_client_id: clientId });

      if (error) throw error;
      return { success: true, data: data?.[0] || null };
    } catch (error: any) {
      console.error('[SEO] Error getting summary:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Recommendations
   */
  async getRecommendations(clientId: string, pageId?: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'rec_1',
            category: 'meta_tags',
            priority: 'critical',
            title: 'Add Meta Description',
            description: 'Missing meta description impacts click-through rates from search results',
            impact_score: 9,
            effort_score: 2,
            current_value: 'None',
            suggested_value: 'Write compelling 120-160 character description',
            is_completed: false
          },
          {
            id: 'rec_2',
            category: 'technical',
            priority: 'high',
            title: 'Add Canonical URL',
            description: 'Canonical URLs prevent duplicate content issues',
            impact_score: 7,
            effort_score: 1,
            current_value: 'None',
            suggested_value: 'Add canonical URL tag',
            is_completed: false
          },
          {
            id: 'rec_3',
            category: 'social',
            priority: 'medium',
            title: 'Add Open Graph Image',
            description: 'OG images improve social media sharing and click-through rates',
            impact_score: 7,
            effort_score: 2,
            current_value: 'None',
            suggested_value: 'Add 1200x630px image URL',
            is_completed: false
          }
        ]
      };
    }

    try {
      let query = supabase
        .from('seo_recommendations')
        .select('*')
        .eq('client_id', clientId)
        .order('priority', { ascending: false });

      if (pageId) {
        query = query.eq('page_id', pageId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[SEO] Error getting recommendations:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  async generateRecommendations(pageId: string) {
    if (USE_MOCK_DATA) {
      console.log('[SEO] Mock mode: Would generate recommendations', pageId);
      return { success: true, data: [] };
    }

    try {
      const { data, error } = await supabase
        .rpc('generate_seo_recommendations', { p_page_id: pageId });

      if (error) throw error;

      const { data: page } = await supabase
        .from('seo_pages')
        .select('client_id')
        .eq('id', pageId)
        .single();

      if (page && data) {
        const recommendations = data.map((rec: any) => ({
          page_id: pageId,
          client_id: page.client_id,
          ...rec
        }));

        await supabase.from('seo_recommendations').insert(recommendations);
      }

      return { success: true, data };
    } catch (error: any) {
      console.error('[SEO] Error generating recommendations:', error);
      return { success: false, error: error.message };
    }
  }

  async completeRecommendation(recommendationId: string, completedBy: string) {
    if (USE_MOCK_DATA) {
      console.log('[SEO] Mock mode: Would complete recommendation', recommendationId);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('seo_recommendations')
        .update({
          is_completed: true,
          completed_at: new Date().toISOString(),
          completed_by: completedBy
        })
        .eq('id', recommendationId);

      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('[SEO] Error completing recommendation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Templates
   */
  async getTemplates(category?: string, industry?: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'tpl_1',
            name: 'E-commerce Product Page',
            description: 'Optimized meta tags for e-commerce product pages',
            category: 'product',
            industry: 'ecommerce',
            usage_count: 45,
            template_data: {
              title: '[Product Name] | Buy Online | [Brand Name]',
              description: 'Shop [Product Name] at [Brand]. [Key Features]. Free shipping on orders over $50. Order now!',
              og_title: '[Product Name] - [Brand Name]',
              og_description: '[Compelling product description with benefits]',
              twitter_card: 'summary_large_image'
            }
          },
          {
            id: 'tpl_2',
            name: 'Service Page',
            description: 'Meta tags optimized for service pages',
            category: 'service',
            industry: 'general',
            usage_count: 32,
            template_data: {
              title: '[Service Name] | Professional [Service] Services | [City]',
              description: 'Expert [Service] services in [City]. [Key Benefits]. Get a free quote today! Call [Phone] or book online.',
              og_title: 'Professional [Service] Services',
              og_description: '[Service benefits and unique selling points]'
            }
          },
          {
            id: 'tpl_3',
            name: 'Blog Article',
            description: 'SEO-optimized meta tags for blog posts',
            category: 'blog',
            industry: 'general',
            usage_count: 67,
            template_data: {
              title: '[Article Title] | [Blog Name]',
              description: '[Article summary with target keyword]. Read more insights on [Topic].',
              og_type: 'article',
              twitter_card: 'summary_large_image'
            }
          },
          {
            id: 'tpl_4',
            name: 'Local Business Homepage',
            description: 'Meta tags for local business homepages',
            category: 'homepage',
            industry: 'local',
            usage_count: 28,
            template_data: {
              title: '[Business Name] | [Primary Service] in [City, State]',
              description: '[Business Name] provides [services] in [City]. [Years] of experience. [Key Benefits]. Call [Phone] for service.',
              og_type: 'website',
              og_locale: 'en_US'
            }
          }
        ]
      };
    }

    try {
      let query = supabase
        .from('seo_templates')
        .select('*')
        .eq('is_public', true);

      if (category) query = query.eq('category', category);
      if (industry) query = query.eq('industry', industry);

      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[SEO] Error getting templates:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  async applyTemplate(pageId: string, templateId: string, clientId: string) {
    if (USE_MOCK_DATA) {
      console.log('[SEO] Mock mode: Would apply template', templateId, pageId);
      return { success: true };
    }

    try {
      const { data: template, error: templateError } = await supabase
        .from('seo_templates')
        .select('template_data')
        .eq('id', templateId)
        .single();

      if (templateError) throw templateError;

      const metaTags = {
        page_id: pageId,
        client_id: clientId,
        ...template.template_data
      };

      const { error } = await supabase
        .from('seo_meta_tags')
        .upsert([metaTags]);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('[SEO] Error applying template:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Competitors
   */
  async getCompetitors(clientId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'comp_1',
            competitor_name: 'Competitor A',
            competitor_url: 'https://competitor-a.com',
            competitor_domain: 'competitor-a.com',
            domain_authority: 65,
            organic_keywords: 1200,
            backlinks_count: 5000,
            referring_domains: 350,
            is_tracking: true
          },
          {
            id: 'comp_2',
            competitor_name: 'Competitor B',
            competitor_url: 'https://competitor-b.com',
            competitor_domain: 'competitor-b.com',
            domain_authority: 58,
            organic_keywords: 950,
            backlinks_count: 3200,
            referring_domains: 280,
            is_tracking: true
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('seo_competitors')
        .select('*')
        .eq('client_id', clientId)
        .order('domain_authority', { ascending: false });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[SEO] Error getting competitors:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  async addCompetitor(competitor: any) {
    if (USE_MOCK_DATA) {
      console.log('[SEO] Mock mode: Would add competitor', competitor);
      return { success: true, data: { id: `comp_${Date.now()}`, ...competitor } };
    }

    try {
      const { data, error } = await supabase
        .from('seo_competitors')
        .insert([competitor])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      console.error('[SEO] Error adding competitor:', error);
      return { success: false, error: error.message };
    }
  }

  async analyzeCompetitorGap(clientId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          { metric: 'Keywords', client_value: 150, avg_competitor_value: 200, gap: -50, status: 'behind' },
          { metric: 'Backlinks', client_value: 300, avg_competitor_value: 250, gap: 50, status: 'ahead' },
          { metric: 'Domain Authority', client_value: 45, avg_competitor_value: 55, gap: -10, status: 'behind' }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .rpc('analyze_competitor_gap', { p_client_id: clientId });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[SEO] Error analyzing competitor gap:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Google Search Console
   */
  async syncGSCData(connectionId: string, clientId: string) {
    if (USE_MOCK_DATA) {
      console.log('[SEO] Mock mode: Would sync GSC data', connectionId);
      return { success: true, data: { synced: 100 } };
    }

    try {
      const mockData = {
        connection_id: connectionId,
        client_id: clientId,
        page_url: 'https://example.com/',
        date: new Date().toISOString().split('T')[0],
        clicks: Math.floor(Math.random() * 500),
        impressions: Math.floor(Math.random() * 5000),
        ctr: Math.random() * 0.1,
        position: Math.random() * 20 + 1
      };

      const { error } = await supabase
        .from('google_search_console_data')
        .upsert([mockData]);

      if (error) throw error;
      return { success: true, data: { synced: 1 } };
    } catch (error: any) {
      console.error('[SEO] Error syncing GSC data:', error);
      return { success: false, error: error.message };
    }
  }

  async getGSCData(connectionId: string, days = 30) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: {
          total_clicks: 1250,
          total_impressions: 15000,
          avg_ctr: 0.083,
          avg_position: 12.5,
          top_queries: [
            { query: 'digital marketing', clicks: 250 },
            { query: 'seo services', clicks: 180 }
          ],
          top_pages: [
            { page: 'https://example.com/', clicks: 450 },
            { page: 'https://example.com/about', clicks: 320 }
          ]
        }
      };
    }

    try {
      const { data, error } = await supabase
        .rpc('get_gsc_summary', { p_connection_id: connectionId, p_days: days });

      if (error) throw error;
      return { success: true, data: data?.[0] || null };
    } catch (error: any) {
      console.error('[SEO] Error getting GSC data:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Backlinks
   */
  async getBacklinks(clientId: string) {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: [
          {
            id: 'bl_1',
            source_domain: 'authority-site.com',
            source_url: 'https://authority-site.com/article',
            target_url: 'https://example.com/services',
            anchor_text: 'professional digital marketing',
            domain_authority: 68,
            page_authority: 45,
            link_type: 'dofollow',
            spam_score: 2,
            is_active: true,
            is_toxic: false
          },
          {
            id: 'bl_2',
            source_domain: 'industry-blog.com',
            source_url: 'https://industry-blog.com/resources',
            target_url: 'https://example.com/',
            anchor_text: 'click here',
            domain_authority: 42,
            page_authority: 38,
            link_type: 'dofollow',
            spam_score: 5,
            is_active: true,
            is_toxic: false
          }
        ]
      };
    }

    try {
      const { data, error } = await supabase
        .from('seo_backlinks')
        .select('*')
        .eq('client_id', clientId)
        .eq('is_active', true)
        .order('domain_authority', { ascending: false })
        .limit(100);

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('[SEO] Error getting backlinks:', error);
      return { success: false, error: error.message, data: [] };
    }
  }
}

export const seoService = new SEOService();
